import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity, ArrowLeft, ArrowRight, Pencil, Power } from 'lucide-react';
import {
  CustomerContext,
  CustomerStatus,
  PERMISSIONS,
  type CustomerTimelineEntry,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { ContactPreferencesCard } from '@/modules/crm/components/ContactPreferencesCard';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDateTime } from '@/utils/format';
import { FaceShapeHistory } from '@/modules/face/components/FaceShapeHistory';
import { RecommendFramesLink } from '@/modules/frame-recommendations/components/RecommendFramesLink';
import { MeasurementHistory } from '@/modules/measurements/components/MeasurementHistory';
import { EyeExaminationHistory } from '@/modules/medical/components/EyeExaminationHistory';
import { OpticalOrdersCard } from '@/modules/optical-orders/components/OpticalOrdersCard';
import {
  useChangeCustomerStatusMutation,
  useCustomerCompletenessQuery,
  useCustomerQuery,
  useCustomerReadinessQuery,
  useCustomerTimelineQuery,
} from '../api/customers.api';
import { CustomerProfileCompleteness } from '../components/CustomerProfileCompleteness';
import { CustomerStatusBadge } from '../components/CustomerStatusBadge';
import { CustomerSummaryCard } from '../components/CustomerSummaryCard';
import { EditCustomerDrawer } from '../components/EditCustomerDrawer';

function TimelineList({ entries }: { entries: CustomerTimelineEntry[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <ol className="divide-line divide-y">
      {entries.map((entry) => (
        <li key={entry.id} className="flex items-start gap-3 px-5 py-3">
          <span className="bg-brand-50 text-brand-600 mt-0.5 grid size-7 shrink-0 place-items-center rounded-full">
            <Activity className="size-3.5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-ink text-sm">
              {/* A message entry's action is its CRM status, so it is labelled with the CRM wording rather than shown as a raw code. */}
              {entry.communication
                ? `${t(`crm.purpose.${entry.communication.purpose}`, { defaultValue: entry.communication.purpose })} · ${t(`crm.status.${entry.communication.status}`, { defaultValue: entry.communication.status })}`
                : t(`customers.timeline.${entry.action.toLowerCase()}`, {
                    defaultValue: entry.action,
                  })}
            </p>
            <p className="text-muted mt-0.5 text-xs">
              {formatDateTime(entry.at, locale)}
              {entry.actorName ? ` · ${entry.actorName}` : ''}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function CustomerProfilePage() {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { isRtl } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isEditOpen, setEditOpen] = useState(false);
  const [isStatusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const customerQuery = useCustomerQuery(customerId);
  const completenessQuery = useCustomerCompletenessQuery(customerId);
  const readinessQuery = useCustomerReadinessQuery(customerId, CustomerContext.CUSTOMER_BASIC);
  const timelineQuery = useCustomerTimelineQuery(customerId);
  const changeStatus = useChangeCustomerStatusMutation(customerId ?? '');

  const canEdit = can(PERMISSIONS.CUSTOMERS_EDIT);
  // Reading a customer is `customers.view`; seeing what they were told, or may be told, is the
  // messaging capability. Two questions, two answers.
  const canSeeMessaging = can(PERMISSIONS.NOTIFICATIONS_VIEW);
  const canChangeStatus = can(PERMISSIONS.CUSTOMERS_CHANGE_STATUS);

  // The list page links here with ?edit=1 to open the drawer straight away.
  useEffect(() => {
    if (searchParams.get('edit') === '1' && canEdit) {
      setEditOpen(true);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [canEdit, searchParams, setSearchParams]);

  if (customerQuery.isPending) return <LoadingState />;

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <div className="space-y-4">
        <Alert tone="error">
          {apiErrorMessage(customerQuery.error) ?? t('customers.notFound')}
        </Alert>
        <Button variant="secondary" onClick={() => void navigate('/customers')}>
          {t('customers.backToList')}
        </Button>
      </div>
    );
  }

  const customer = customerQuery.data;
  const isActive = customer.status === CustomerStatus.ACTIVE;
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;

  const confirmStatusChange = () => {
    setStatusError(null);

    changeStatus.mutate(
      { status: isActive ? CustomerStatus.INACTIVE : CustomerStatus.ACTIVE },
      {
        onSuccess: () => setStatusDialogOpen(false),
        onError: (error) => setStatusError(apiErrorMessage(error) ?? t('customers.status.error')),
      },
    );
  };

  return (
    <div className="space-y-6">
      <Link
        to="/customers"
        className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
      >
        <BackIcon className="size-4" aria-hidden />
        {t('customers.backToList')}
      </Link>

      <PageHeader
        title={customer.displayName}
        subtitle={customer.customerNumber}
        actions={
          <>
            <CustomerStatusBadge status={customer.status} />

            <RecommendFramesLink customerId={customer.id} />

            {canEdit ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                <Pencil className="size-4" aria-hidden />
                {t('customers.edit.action')}
              </Button>
            ) : null}

            {canChangeStatus ? (
              <Button
                variant={isActive ? 'danger' : 'primary'}
                onClick={() => setStatusDialogOpen(true)}
              >
                <Power className="size-4" aria-hidden />
                {isActive ? t('customers.status.deactivate') : t('customers.status.activate')}
              </Button>
            ) : null}
          </>
        }
      />

      {statusError ? <Alert tone="error">{statusError}</Alert> : null}

      {!isActive ? <Alert tone="warning">{t('customers.status.inactiveNotice')}</Alert> : null}

      <CustomerSummaryCard customer={customer} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('customers.completeness.title')}
            subtitle={
              readinessQuery.data?.ready ? t('customers.completeness.readyForBasic') : undefined
            }
          />
          <CustomerProfileCompleteness
            completeness={completenessQuery.data}
            isLoading={completenessQuery.isPending}
            canEdit={canEdit}
            onComplete={() => setEditOpen(true)}
          />
        </Card>

        <MeasurementHistory customerId={customerId ?? ''} />

        <FaceShapeHistory customerId={customerId ?? ''} />

        {/* Whether this customer may be messaged belongs to the customer, not to a CRM settings
            screen: it is something they said about themselves. */}
        {canSeeMessaging ? <ContactPreferencesCard customerId={customerId ?? ''} /> : null}

        <Card>
          <CardHeader title={t('customers.sections.activity')} />

          {timelineQuery.isPending ? (
            <LoadingState />
          ) : timelineQuery.data && timelineQuery.data.length > 0 ? (
            <TimelineList entries={timelineQuery.data} />
          ) : (
            <EmptyState
              icon={Activity}
              title={t('customers.timeline.empty')}
              className="px-5 py-10"
            />
          )}
        </Card>
      </div>

      {/* Optical orders start here for anyone who may take one; eye examinations appear beside them for anyone who may read clinical records. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <OpticalOrdersCard customerId={customer.id} />
        <EyeExaminationHistory customerId={customerId ?? ''} />
      </div>

      {canEdit ? (
        <EditCustomerDrawer
          customer={customer}
          isOpen={isEditOpen}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

      {/* Not rendered at all without the permission: a closed dialog still puts its markup in the document, and there is no reason to ship a control this user can never use. */}
      {canChangeStatus ? (
        <ConfirmDialog
          isOpen={isStatusDialogOpen}
          onCancel={() => setStatusDialogOpen(false)}
          onConfirm={confirmStatusChange}
          isConfirming={changeStatus.isPending}
          isDestructive={isActive}
          title={
            isActive ? t('customers.status.confirmTitle') : t('customers.status.activateTitle')
          }
          message={
            isActive ? t('customers.status.confirmMessage') : t('customers.status.activateMessage')
          }
          confirmLabel={
            isActive ? t('customers.status.deactivate') : t('customers.status.activate')
          }
        />
      ) : null}
    </div>
  );
}
